import { azureScopes } from '@/app.azure';
import { deofuscate, ofuscate } from '@/utils/obfuscation';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import { exhaustMap, map } from 'rxjs/operators';
import { PrivilegeKeys, Privileges, privilegesDictionary, RoleKeys, Roles, rolesDictionary } from '../enum/auth.enum';
import { LoginResponse, Me, RolesAndPrivileges, RolesAndPrivilegesFromBackend } from '../interface/auth.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  constructor(
    private http: HttpClient,
    private msalAuthService: MsalService,
  ) { }

  getMe(): Observable<Me> {
    return this.http.get<RolesAndPrivilegesFromBackend>('/User/UserProfile?mdmid=')
      .pipe(
        map((data) => ({
          mdmid: data.mdmid,
          roles: data.roles.map((role) => ({
            id: role.id,
            assetId: role.assetId,
            name: role.roleName,
            description: role.description,
          })),
          privileges: data.privilegeType.map((privilege) => ({
            id: privilege.id,
            name: privilege.privilegeName,
            description: privilege.privilegeDescription,
          })),
        })),
        exhaustMap(
          (data) =>
            this.http.get<LoginResponse[]>(`/User/UserInfo/${data.mdmid}`)
              .pipe(
                map(([userData]) => ({ ...userData, ...data })),
              ),
        ),
      );
  }

  private getRolesAndPrivilegesFromLocal(): RolesAndPrivileges {
    return deofuscate(localStorage.getItem('session') || '');
  }

  getMyRole(): RolesAndPrivileges['roles'][0] {
    return this.getRolesAndPrivilegesFromLocal().roles[0];
  }

  getRoles() {
    return this.getRolesAndPrivilegesFromLocal().roles;
  }
  getRoleNameById(id: number | string): Roles {
    return rolesDictionary[<unknown>`${id}` as RoleKeys];
  }
  getRoleIdByName(roleName: Roles) {
    return Object.keys(rolesDictionary).reduce((acc, key) => {
      const currentRoleName = this.getRoleNameById(key);
      if (roleName === currentRoleName) {
        return key;
      }
      return acc;
    });
  }

  getUserRoleName(): Roles {
    const userRolesIds = this.getRolesAndPrivilegesFromLocal().roles.map(({ id }) => +id);

    const roleKey = Object.keys(rolesDictionary).map((id) => +id).find((id) => userRolesIds.includes(id)) as RoleKeys;

    return rolesDictionary[roleKey];
  }

  getUserRoleId(): number {
    const userRolesIds = this.getRolesAndPrivilegesFromLocal().roles.map(({ id }) => +id);
    return userRolesIds[0];
  }

  userHavePrivilege(necessaryPrivilegeName: Privileges): boolean {
    const rolesAndPrivileges = this.getRolesAndPrivilegesFromLocal();

    if (!rolesAndPrivileges) {
      return false;
    }

    const necessaryPrivilegeIds = Object.keys(privilegesDictionary).filter((stringKey) => {
      const key = <unknown>stringKey as PrivilegeKeys;
      const privilegeName = privilegesDictionary[key];
      if (privilegeName === necessaryPrivilegeName) {
        return true;
      }
      if (privilegeName === '_SUPER_ADMIN') {
        return true;
      }

      return false;
    }).map((id) => +id);

    const userPrivilegeIds = rolesAndPrivileges.privileges.map(({ id }) => +id);

    const havePrivileges = necessaryPrivilegeIds.some((id) => userPrivilegeIds.includes(id));

    return havePrivileges;
  }

  userHaveRole(necessaryRole: Roles): boolean {
    const rolesAndPrivileges = this.getRolesAndPrivilegesFromLocal();

    if (!rolesAndPrivileges) {
      return false;
    }

    const necessaryRolesIds = Object.keys(rolesDictionary).filter((stringKey) => {
      const key = <unknown>stringKey as RoleKeys;
      const roleName = rolesDictionary[key];
      if (roleName === necessaryRole) {
        return true;
      }
      if (roleName === 'DLMS_OWNER_MEL') {
        return true;
      }
      if (roleName === 'DLMS_OWNER_SPC') {
        return true;
      }

      return false;
    }).map((id) => +id);

    const userRolesIds = rolesAndPrivileges.roles.map(({ id }) => +id);

    const havePrivileges = necessaryRolesIds.some((id) => userRolesIds.includes(id));

    return havePrivileges;
  }

  userHaveRoleClean(necessaryRole: Roles): boolean {
    const rolesAndPrivileges = this.getRolesAndPrivilegesFromLocal();

    if (!rolesAndPrivileges) {
      return false;
    }

    const necessaryRolesIds = Object.keys(rolesDictionary).filter((stringKey) => {
      const key = <unknown>stringKey as RoleKeys;
      const roleName = rolesDictionary[key];
      if (roleName === necessaryRole) {
        return true;
      }
      return false;
    }).map((id) => +id);

    const userRolesIds = rolesAndPrivileges.roles.map(({ id }) => +id);

    const havePrivileges = necessaryRolesIds.some((id) => userRolesIds.includes(id));

    return havePrivileges;
  }


  logout() {
    sessionStorage.clear();
    localStorage.clear();
    this.msalAuthService.acquireTokenRedirect({ scopes: azureScopes, prompt: 'login' });
  }

  getUserAssetId(): number {
    return this.getRolesAndPrivilegesFromLocal().roles.reduce((findedAssetId, { assetId }) => {
      if (findedAssetId) {
        return findedAssetId;
      }
      return assetId || 0;
    }, 0);
  }

  addToLogLoginSuccessfully(params: any) {
    console.log(params);
    // this.http.post<LoginResponse>(`Auth/login`, { user: email, password: email }).subscribe({
    //   next: () => {

    //   },
    // });
  }

  addPrivilegeLocally(privilegeToAdd: Privileges) {
    const necessaryPrivilegeId = Object.keys(privilegesDictionary).filter((stringKey) => {
      const key = <unknown>stringKey as PrivilegeKeys;
      const privilegeName = privilegesDictionary[key];
      if (privilegeName === privilegeToAdd) {
        return true;
      }
      if (privilegeName === '_SUPER_ADMIN') {
        return true;
      }

      return false;
    }).map((id) => +id)[0];

    const rolesAndPrivileges = this.getRolesAndPrivilegesFromLocal();

    if (!this.userHavePrivilege(privilegeToAdd)) {
      rolesAndPrivileges.privileges.push({ id: +necessaryPrivilegeId, name: 'temp', description: 'temp' });
      const ofuscatedRolesAndPrivileges = ofuscate(rolesAndPrivileges);
      localStorage.setItem('session', ofuscatedRolesAndPrivileges);
    }

  }

  removePrivilegeLocally(privilegeToAdd: Privileges) {
    const necessaryPrivilegeId = Object.keys(privilegesDictionary).filter((stringKey) => {
      const key = <unknown>stringKey as PrivilegeKeys;
      const privilegeName = privilegesDictionary[key];
      if (privilegeName === privilegeToAdd) {
        return true;
      }
      if (privilegeName === '_SUPER_ADMIN') {
        return true;
      }

      return false;
    }).map((id) => +id)[0];

    const rolesAndPrivileges = this.getRolesAndPrivilegesFromLocal();

    if (this.userHavePrivilege(privilegeToAdd)) {
      const newPrivileges = rolesAndPrivileges.privileges.filter(({ id, name }) => !(id === +necessaryPrivilegeId && name === 'temp'));
      if (newPrivileges.length !== rolesAndPrivileges.privileges.length) {
        rolesAndPrivileges.privileges = newPrivileges;
        const ofuscatedRolesAndPrivileges = ofuscate(rolesAndPrivileges);
        localStorage.setItem('session', ofuscatedRolesAndPrivileges);
      }
    }
  }
}
